import { useEffect, useState } from 'react'
import { useOnlineStatus } from '../hooks/useOnlineStatus'

/**
 * Banner that shows when the app is offline
 * Appears at the top of the screen with a subtle animation
 */
export function OfflineBanner() {
  const { isOnline, checkConnection } = useOnlineStatus()
  const [showBanner, setShowBanner] = useState(false)
  const [isChecking, setIsChecking] = useState(false)

  // Debounce the banner display to prevent flickering
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>

    if (!isOnline) {
      // Show banner immediately when going offline
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Syncing banner visibility with online status
      setShowBanner(true)
    } else {
      // Delay hiding banner to show "Back online" message
      timeoutId = setTimeout(() => {
        setShowBanner(false)
      }, 2000)
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [isOnline])

  const handleRetry = async () => {
    setIsChecking(true)
    await checkConnection()
    setIsChecking(false)
  }

  if (!showBanner) {
    return null
  }

  return (
    <div
      className={`
        fixed top-0 left-0 right-0 z-50 px-4 py-3
        transition-all duration-300 ease-out
        ${isOnline
          ? 'bg-green-600 text-white'
          : 'bg-amber-500 text-amber-950'
        }
      `}
      role="alert"
      aria-live="polite"
    >
      <div className="max-w-lg mx-auto flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {/* Status icon */}
          {isOnline ? (
            <svg
              className="w-5 h-5 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
              />
            </svg>
          ) : (
            <svg
              className="w-5 h-5 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414"
              />
            </svg>
          )}

          {/* Message */}
          <span className="text-sm font-medium truncate">
            {isOnline
              ? 'Back online! Syncing data...'
              : 'You\'re offline. Changes will sync when reconnected.'
            }
          </span>
        </div>

        {/* Retry button (only when offline) */}
        {!isOnline && (
          <button
            onClick={handleRetry}
            disabled={isChecking}
            className="
              flex-shrink-0 px-3 py-1.5 text-sm font-medium
              bg-amber-600 hover:bg-amber-700 rounded-lg
              transition-colors active:scale-95
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            {isChecking ? (
              <span className="flex items-center gap-1.5">
                <svg
                  className="w-4 h-4 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Checking
              </span>
            ) : (
              'Retry'
            )}
          </button>
        )}
      </div>
    </div>
  )
}
